import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UseFormReturn } from "react-hook-form";
import { PetInput } from "@/lib/validations/pet";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, normalizeDecimalInput, parseOptionalDecimalInput } from "@/lib/utils";
import { PetDaycareHotelFields } from "@/components/pets/daycare-hotel/PetDaycareHotelFields";
import {
  usePetGenders,
  usePetSterilizations,
  useSpecies,
  useBreeds,
  useCoatTypes,
  useColors,
  usePetStatuses,
  useConditions,
  useTemperaments,
} from "@/hooks/useLookups";
import { PetPhotoUpload } from "@/components/pets/PetPhotoUpload";
import { CustomIcons } from "../icons/CustomIcons";
import { EyeOff } from "lucide-react";
import { BreedAutocomplete } from "@/components/patients/BreedAutocomplete";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { useToast } from "@/hooks/use-toast";
import { PetBirthDateAgeField } from "@/components/pets/PetBirthDateAgeField";
import { useTranslation } from "react-i18next";
import { DECEASED_STATUS_NAME, normalizePetStatusName } from "@/lib/utils/pet-death";
import { usePetSizeCategories } from "@/hooks/usePetGroomingProfile";

interface PetFormSectionProps {
  form: UseFormReturn<PetInput>;
  index: number;
  onRemove?: () => void;
  showRemoveButton?: boolean;
  photoAllowReplace?: boolean;
  recordNumberUi?: { disabled: boolean; placeholder: string };
  petId?: string | null;
}

export function PetFormSection({
  form,
  index,
  onRemove,
  showRemoveButton = false,
  photoAllowReplace = false,
  recordNumberUi,
  petId,
}: PetFormSectionProps) {
  const { t } = useTranslation("patients");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedSpecies, setSelectedSpecies] = useState<string>(
    form.getValues("species_id") || ""
  );
  const [breedDisplayValue, setBreedDisplayValue] = useState("");
  const isTypingBreedRef = useRef(false);
  const [colorDisplayValue, setColorDisplayValue] = useState("");
  const [currentWeightDisplay, setCurrentWeightDisplay] = useState(
    () => {
      const currentWeight = form.getValues("current_weight");
      return typeof currentWeight === "number" ? String(currentWeight) : "";
    },
  );
  const [isCreatingBreed, setIsCreatingBreed] = useState(false);
  const scrollToTopOnError = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const { data: petGenders } = usePetGenders();
  const { data: sterilizations } = usePetSterilizations();
  const { data: species } = useSpecies();
  const { data: breeds, isLoading: isBreedsLoading } = useBreeds(selectedSpecies);
  const { data: coatTypes } = useCoatTypes();
  const { data: colors } = useColors();
  const { data: statuses } = usePetStatuses();
  const { data: allergies } = useConditions("allergy");
  const { data: temperaments } = useTemperaments();
  const { data: sizeCategories } = usePetSizeCategories();

  // Watch form values to sync state when form is reset with pet data
  const speciesId = form.watch("species_id");
  const breedId = form.watch("breed_id");
  const colorId = form.watch("color_id");
  const statusId = form.watch("status_id");
  const currentWeightValue = form.watch("current_weight");
  const selectedStatus = statuses?.find((status) => status.id === statusId) ?? null;
  const isDeceasedStatus =
    normalizePetStatusName(selectedStatus?.name) === DECEASED_STATUS_NAME.toLowerCase();
  // Update selectedSpecies when species_id changes
  useEffect(() => {
    if (speciesId) {
      setSelectedSpecies(speciesId);
    } else {
      // Reset if species_id is cleared
      setSelectedSpecies("");
    }
  }, [speciesId]);

  // Update breedDisplayValue when breed_id or breeds data changes
  useEffect(() => {
    if (breedId && breeds) {
      const selectedBreed = breeds.find((breed) => breed.id === breedId);
      if (selectedBreed) {
        setBreedDisplayValue(selectedBreed.name);
      }
    } else if (!breedId && !isTypingBreedRef.current) {
      setBreedDisplayValue("");
    }
  }, [breedId, breeds]);

  // Update colorDisplayValue when color_id or colors data changes
  useEffect(() => {
    if (colorId && colors) {
      const selectedColor = colors.find((color) => color.id === colorId);
      if (selectedColor) {
        setColorDisplayValue(selectedColor.name);
      }
    } else if (!colorId) {
      setColorDisplayValue("");
    }
  }, [colorId, colors]);

  useEffect(() => {
    if (isDeceasedStatus) return;
    if (form.getValues("death_date")) {
      form.setValue("death_date", "");
    }
    if (form.getValues("death_cause")) {
      form.setValue("death_cause", "");
    }
  }, [form, isDeceasedStatus]);

  useEffect(() => {
    if (typeof currentWeightValue === "number" && currentWeightDisplay === "") {
      setCurrentWeightDisplay(String(currentWeightValue));
    }
  }, [currentWeightDisplay, currentWeightValue]);

  const createBreed = useCallback(
    async (name: string, onSelectBreedId: (breedId: string) => void) => {
      const trimmedName = name.trim();
      if (!trimmedName) return;

      if (!selectedSpecies) {
        toast({
          title: t("petForm.selectSpeciesFirstTitle"),
          description: t("petForm.selectSpeciesFirstDescription"),
          variant: "destructive",
        });
        scrollToTopOnError();
        return;
      }

      setIsCreatingBreed(true);
      try {
        // Ensure a clinic is selected (required for customer-scoped inserts)
        await getCurrentClinicCustomerId();

        const { data, error } = await supabase
          .from("breeds")
          .insert({
            name: trimmedName,
            species_id: selectedSpecies,
          })
          .select("*")
          .single();

        if (error) throw error;

        const created = data as { id: string; name: string };
        onSelectBreedId(created.id);
        setBreedDisplayValue(created.name);
        form.clearErrors("breed_id");
        queryClient.invalidateQueries({ queryKey: ["breeds"] });

        toast({
          title: t("petForm.breedCreatedTitle"),
          description: t("petForm.breedCreatedDescription", { name: created.name }),
        });
      } catch (error: unknown) {
        console.error("Erro ao cadastrar raça:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : typeof (error as { message?: unknown } | null)?.message === "string"
              ? String((error as { message: string }).message)
              : "";
        const message =
          errorMessage.toLowerCase().includes("duplicate")
            ? t("petForm.breedAlreadyExists")
            : t("petForm.breedCreateError");

        toast({
          title: t("petForm.breedCreateErrorTitle"),
          description: message,
          variant: "destructive",
        });
        scrollToTopOnError();
      } finally {
        setIsCreatingBreed(false);
      }
    },
    [form, queryClient, scrollToTopOnError, selectedSpecies, toast, t],
  );

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-card relative">
      {showRemoveButton && onRemove && (
        <Button
          type="button"
          variant="ghost"
          className="hover:!text-destructive !absolute !top-2 !right-2"
          onClick={onRemove}
        >
          <CustomIcons name="trash" className="!h-5 !w-5 text-inherit" />
        </Button>
      )}

      <div className="space-y-4">
        {/* Nome do Pet */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.patientName")} <span className="text-destructive ">*</span></FormLabel>
                <FormControl>
                  <Input placeholder={t("petForm.placeholders.patientName")} {...field} data-pet-name-input />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="record_number"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.recordNumber")}</FormLabel>
                <FormControl>
                  {(() => {
                    const isEmpty = String(field.value ?? "").trim().length === 0;
                    const shouldDisable = Boolean(recordNumberUi?.disabled) && isEmpty;
                    const placeholder = shouldDisable
                      ? (recordNumberUi?.placeholder ?? t("petForm.placeholders.loading"))
                      : recordNumberUi?.disabled
                        ? t("petForm.placeholders.recordNumber")
                        : (recordNumberUi?.placeholder ?? t("petForm.placeholders.recordNumber"));

                    return (
                      <Input
                        placeholder={placeholder}
                        disabled={shouldDisable}
                        className={cn(
                          fieldState.error && "border-destructive focus-visible:ring-destructive"
                        )}
                        {...field}
                        onChange={(event) => {
                          field.onChange(event);
                          // If a server-side validation error was shown, clear it as user edits.
                          if (form.formState.errors.record_number) {
                            form.clearErrors("record_number");
                          }
                        }}
                      />
                    );
                  })()}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Espécie e Raça */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="species_id"
            data-testid="species-select"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.species")}  <span className="text-destructive ">*</span></FormLabel>
                <Select
                  name="species_id"
                  onValueChange={(value) => {
                    field.onChange(value);
                    setSelectedSpecies(value);
                    isTypingBreedRef.current = false;
                    setBreedDisplayValue("");
                    form.setValue("breed_id", "");
                    form.clearErrors("species_id");
                  }}
                  value={field.value ?? ""}
                  key={`species-${species?.length || 0}-${field.value || ""}`}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("petForm.placeholders.select")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {species?.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="breed_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.breed")}</FormLabel>
                <FormControl>
                  <BreedAutocomplete
                    breeds={(breeds ?? []).map((breed) => ({ id: breed.id, name: breed.name }))}
                    isLoading={isBreedsLoading}
                    value={breedDisplayValue}
                    selectedBreedId={field.value || ""}
                    onChange={(value) => {
                      isTypingBreedRef.current = true;
                      // Update display value as user types
                      setBreedDisplayValue(value);

                      // Check if typed value matches a breed exactly
                      const matchedBreed = breeds?.find(
                        (breed) => breed.name.toLowerCase() === value.toLowerCase()
                      );

                      if (matchedBreed) {
                        // Auto-select if exact match
                        field.onChange(matchedBreed.id);
                        form.clearErrors("breed_id");
                      } else {
                        // Clear breed_id if no match
                        field.onChange("");
                        // DON'T show error here - wait for onBlur
                      }
                    }}
                    onSelectBreed={(breed, displayName) => {
                      isTypingBreedRef.current = false;
                      field.onChange(breed.id);
                      setBreedDisplayValue(displayName);
                      form.clearErrors("breed_id");
                    }}
                    onCreateNew={(name) => createBreed(name, field.onChange)}
                    isCreating={isCreatingBreed}
                    onBlur={() => {
                      isTypingBreedRef.current = false;
                      // VALIDATION ONLY ON BLUR
                      if (breedDisplayValue.trim() && !field.value && selectedSpecies) {
                        form.setError("breed_id", {
                          type: "manual",
                          message: t("petForm.placeholders.breedInvalid"),
                        });
                      } else {
                        form.clearErrors("breed_id");
                      }
                    }}
                    placeholder={
                      selectedSpecies
                        ? t("petForm.placeholders.typeBreed")
                        : t("petForm.placeholders.selectSpeciesFirst")
                    }
                    emptyMessage={
                      selectedSpecies ? (
                        <div className="px-3 py-4 text-sm text-muted-foreground">
                          <p>{t("petForm.placeholders.noBreedFound")}</p>
                        </div>
                      ) : (
                        <div className="px-3 py-4 text-sm text-muted-foreground">
                          <p>{t("petForm.placeholders.selectSpeciesFirst")}</p>
                        </div>
                      )
                    }
                    disabled={!selectedSpecies}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Gênero e Porte */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="gender_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.gender")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ""} key={`gender-${petGenders?.length || 0}-${field.value || ""}`}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("petForm.placeholders.select")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {petGenders?.map((gender) => (
                      <SelectItem key={gender.id} value={gender.id}>
                        {gender.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="size_category_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.sizeCategory")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ""}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("petForm.placeholders.selectSizeCategory")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sizeCategories?.map((category) => (
                      <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Nascimento e Idade */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <FormField
            control={form.control}
            name="birth_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.birthDate")}</FormLabel>
                <PetBirthDateAgeField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  calendarButtonClassName="!w-10 !h-10"
                  inputClassName="!flex-1"
                  showBirthDateOnly
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="birth_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.age")}</FormLabel>
                <PetBirthDateAgeField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  calendarButtonClassName="!w-10 !h-10"
                  inputClassName="!flex-1"
                  showAgeOnly
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Tamanho do pelo, Cor e Status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="coat_type_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.coatType")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ""} key={`coat-type-${coatTypes?.length || 0}-${field.value || ""}`}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("petForm.placeholders.select")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {coatTypes?.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="color_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.color")}</FormLabel>
                <FormControl>
                  <Autocomplete
                    options={(colors ?? []).map((color) => ({
                      label: color.name,
                      value: color.id,
                    }))}
                    value={colorDisplayValue}
                    onChange={(value) => {
                      // Update display value as user types
                      setColorDisplayValue(value);

                      // Check if typed value matches a color exactly
                      const matchedColor = colors?.find(
                        (color) => color.name.toLowerCase() === value.toLowerCase()
                      );

                      if (matchedColor) {
                        // Auto-select if exact match
                        field.onChange(matchedColor.id);
                        form.clearErrors("color_id");
                      } else {
                        // Clear color_id if no match
                        field.onChange("");
                        // DON'T show error here - wait for onBlur
                      }
                    }}
                    onSelect={(option) => {
                      // When user selects from dropdown
                      field.onChange(option.value);
                      setColorDisplayValue(option.label);
                      form.clearErrors("color_id");
                    }}
                    onBlur={() => {
                      // VALIDATION ONLY ON BLUR
                      if (colorDisplayValue.trim() && !field.value) {
                        form.setError("color_id", {
                          type: "manual",
                          message: t("petForm.placeholders.colorInvalid"),
                        });
                      } else {
                        form.clearErrors("color_id");
                      }
                    }}
                    placeholder={t("petForm.placeholders.typeColor")}
                    emptyMessage={t("petForm.placeholders.noColorFound")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="status_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.status")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ""} key={`status-${statuses?.length || 0}-${field.value || ""}`}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("petForm.placeholders.select")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {statuses?.map((status) => (
                      <SelectItem key={status.id} value={status.id}>
                        {status.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {isDeceasedStatus ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="death_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("petForm.fields.deathDate")} <span className="text-destructive">*</span></FormLabel>
                  <PetBirthDateAgeField
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    calendarButtonClassName="!w-10 !h-10"
                    inputClassName="!flex-1"
                    showBirthDateOnly
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="death_cause"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("petForm.fields.deathCause")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("petForm.placeholders.deathCause")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ) : null}

        {/* Castrado e Peso */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="sterilization_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.sterilized")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ""} key={`sterilization-${sterilizations?.length || 0}-${field.value || ""}`}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("petForm.placeholders.select")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sterilizations?.map((sterilization) => (
                      <SelectItem key={sterilization.id} value={sterilization.id}>
                        {sterilization.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="current_weight"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.currentWeight")}</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder={t("petForm.placeholders.weight")}
                    value={currentWeightDisplay}
                    onChange={(event) => {
                      const normalized = normalizeDecimalInput(event.target.value, 3);
                      const parsed = parseOptionalDecimalInput(normalized);
                      const nextValue =
                        typeof parsed === "number" ? Math.min(parsed, 9999) : undefined;
                      const nextDisplay =
                        typeof parsed === "number" && parsed > 9999
                          ? "9999"
                          : normalized;

                      setCurrentWeightDisplay(nextDisplay);
                      field.onChange(
                        nextValue,
                      );
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Microchip */}
          <FormField
            control={form.control}
            name="microchip_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.microchip")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("petForm.placeholders.microchip")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="health_plan"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("petForm.fields.healthPlan")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("petForm.placeholders.healthPlan")}
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Alergias */}
        <div>
          <FormLabel>{t("petForm.fields.allergies")}</FormLabel>
          <p className="text-sm text-muted-foreground mb-3">
            {t("petForm.allergiesHelp")}
          </p>
          <div className="flex flex-wrap gap-2">
            {allergies?.map((allergy) => {
              const isSelected = form.watch("allergies")?.includes(allergy.id);
              return (
                <Badge
                  key={allergy.id}
                  variant={isSelected ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => {
                    const current = form.getValues("allergies") || [];
                    if (isSelected) {
                      form.setValue(
                        "allergies",
                        current.filter((id) => id !== allergy.id)
                      );
                    } else {
                      form.setValue("allergies", [...current, allergy.id]);
                    }
                  }}
                >
                  {allergy.name}
                </Badge>
              );
            })}
          </div>
        </div>

        {/* Temperamento */}
        <div>
          <FormLabel>{t("petForm.fields.temperament")}</FormLabel>
          <p className="text-sm text-muted-foreground mb-3">
            {t("petForm.temperamentHelp")}
          </p>
          <div className="flex flex-wrap gap-2">
            {temperaments?.map((temperament) => {
              const isSelected = form.watch("temperaments")?.includes(temperament.id);
              return (
                <Badge
                  key={temperament.id}
                  variant={isSelected ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => {
                    const current = form.getValues("temperaments") || [];
                    if (isSelected) {
                      form.setValue(
                        "temperaments",
                        current.filter((id) => id !== temperament.id)
                      );
                    } else {
                      form.setValue("temperaments", [...current, temperament.id]);
                    }
                  }}
                >
                  {temperament.name}
                </Badge>
              );
            })}
          </div>
        </div>

        {/* Observações */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>{t("petForm.fields.internalNotes")}</FormLabel>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-color-medium">
                  <EyeOff className="h-3.5 w-3.5 text-color-medium" />
                  {t("petForm.privateBadge")}
                </span>
              </div>
              <FormControl>
                <Textarea
                  placeholder={t("petForm.placeholders.notes")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Foto */}
        <div>
          <FormLabel>{t("petForm.fields.petPhoto")}</FormLabel>
          <p className="text-sm text-muted-foreground mb-3">
            {t("petForm.photoHelp")}
          </p>
          <FormField
            control={form.control}
            name="photo"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PetPhotoUpload
                    value={field.value}
                    onChange={(value) => {
                      // Atualizar o campo usando field.onChange para que o React Hook Form rastreie a mudança
                      field.onChange(value);
                      // Também usar setValue para garantir que o formulário seja marcado como dirty
                      // Isso é crítico quando o valor muda de string (URL) para File
                      form.setValue("photo", value, {
                        shouldDirty: true,
                        shouldValidate: false,
                        shouldTouch: true,
                      });
                    }}
                    allowReplace={photoAllowReplace}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <PetDaycareHotelFields
          petId={petId ?? null}
          value={
            form.watch("daycare_hotel") ?? {
              notes: form.watch("daycare_hotel_notes") ?? "",
              packIds: [],
              alerts: [],
              storedItems: [],
              relationships: [],
              recurringActivities: [],
            }
          }
          onChange={(draft) => {
            form.setValue("daycare_hotel", draft, { shouldDirty: true });
            form.setValue("daycare_hotel_notes", draft.notes, { shouldDirty: true });
            if (!form.getValues("daycare_hotel_initial")) {
              form.setValue("daycare_hotel_initial", draft);
            }
          }}
        />
      </div>
    </div>
  );
}
